import type { Attempt } from '@ranked-waiting/game-core';
import { errorPercent, resultTier, tierLabel } from './result-badge';
import { formatSignedOffsetMs } from './format';

/**
 * Half-width of the visible bar, in percent-of-target error. The tier bands
 * run gold (±0.25%) → green (±1%) → yellow (±2.5%) → orange (±5%) → red (±8%)
 * → way-off (beyond); the track shows the full ±10% so red still has visible
 * room before the marker clamps to the edge (owner decision 2026-08-18:
 * roughly double the old 1/3/5% bands and add a gold bullseye zone). An
 * attempt further off than this pins to the very edge, fully way-off, rather
 * than overflowing the track — so a 41%-off miss on a 5s target reads as
 * hard-against-the-edge, exactly where the owner expected it (report: a badly
 * missed attempt was landing mid-green, which made no sense).
 */
const BAR_RANGE_PCT = 10;

/**
 * A horizontal tolerance-band bar with the attempt PLOTTED on it: the target
 * sits dead centre, colour bands mirror outward, and a bright full-height
 * needle marks exactly where the attempt landed — its position driven
 * directly by how far off the attempt was, as a percentage of the target
 * (owner decision, 2026-08-14; rebuilt 2026-08-18 to widen the bands, add a
 * gold bullseye zone, and slam the needle into place so the reveal reads
 * instantly and with punch, not as a static page).
 */
export function accuracyBarMarkup(attempt: Attempt): string {
  if (attempt.isFalseStart) return '';
  const signedPercent = attempt.targetMs > 0 ? ((attempt.elapsedMs - attempt.targetMs) / attempt.targetMs) * 100 : 0;
  const clamped = Math.max(-BAR_RANGE_PCT, Math.min(BAR_RANGE_PCT, signedPercent));
  const markerPercent = 50 + (clamped / BAR_RANGE_PCT) * 50;
  const tier = resultTier(attempt);
  const label = formatSignedOffsetMs(attempt.elapsedMs, attempt.targetMs);
  const pct = errorPercent(attempt);
  const direction = signedPercent > 0.05 ? 'late' : signedPercent < -0.05 ? 'early' : 'exact';
  const readout =
    direction === 'exact' ? 'Dead on the target' : `${pct.toFixed(1)}% ${direction === 'late' ? 'too late' : 'too early'}`;

  return `
    <div class="accuracy-bar accuracy-bar--${tier}" style="--marker-pos:${markerPercent.toFixed(2)}%">
      <div
        class="accuracy-bar__track"
        role="img"
        aria-label="Accuracy: ${label}, ${pct.toFixed(2)}% ${direction === 'exact' ? 'off target' : direction}"
      >
        <span class="accuracy-bar__center" aria-hidden="true"></span>
        <span class="accuracy-bar__marker accuracy-bar__marker--${tier}" aria-hidden="true">
          <span class="accuracy-bar__marker-head"></span>
        </span>
      </div>
      <div class="accuracy-bar__readout">
        <strong class="accuracy-bar__tier accuracy-bar__tier--${tier}">${tierLabel(tier)}</strong>
        <span class="accuracy-bar__value">${label}</span>
        <span class="accuracy-bar__delta">${readout}</span>
      </div>
    </div>
  `;
}
